import type { LumberRail } from "building-types/framing/lumberTypes";
import type { Framing, GeneratedFramedWallAssembly, WallFramingInput } from "building-types/framing/wallFramingTypes";
import { lumberRailNew } from "doc/utils/lumberTools";
import { mat4Dot, mat4DotSub3 } from "geom/mat4";
import type { Mat4 } from "geom/mat4";
import { planeCopy, planeMoveAlongNormal, planeToPlaneMat4 } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polyLine3New } from "geom/polyLine3";
import type { PolyLine3 } from "geom/polyLine3";
import { transformPlane } from "geom/transform";
import { vec3Dot, vec3Len, vec3Normalize } from "geom/vec3";

const ORTHONORMAL_TOLERANCE = 1e-6;

function assertOrthonormalPlane(plane: Plane, label: string): void {
  const uLen = vec3Len(plane.uX, plane.uY, plane.uZ);
  const vLen = vec3Len(plane.vX, plane.vY, plane.vZ);
  const uvDot = vec3Dot(plane.uX, plane.uY, plane.uZ, plane.vX, plane.vY, plane.vZ);
  if (
    Math.abs(uLen - 1) > ORTHONORMAL_TOLERANCE ||
    Math.abs(vLen - 1) > ORTHONORMAL_TOLERANCE ||
    Math.abs(uvDot) > ORTHONORMAL_TOLERANCE
  ) {
    throw new Error(
      `${label} must have an orthonormal UV basis ` +
        `(|U|=${uLen.toFixed(6)}, |V|=${vLen.toFixed(6)}, U·V=${uvDot.toFixed(6)})`,
    );
  }
}

export function resolveWallFramingTargetPlane(input: WallFramingInput, canonicalPlane: Plane): Plane | null {
  const offset = input.framingPlaneNormalOffset ?? 0;
  if (!input.framingPlane && offset === 0) {
    return null;
  }
  const base = planeCopy(input.framingPlane ?? canonicalPlane);
  return offset !== 0 ? planeMoveAlongNormal(base, offset) : base;
}

function transformPolyLine3(pl: PolyLine3, mat: Mat4): PolyLine3 {
  const out = polyLine3New(pl.length);
  for (let i = 0; i < pl.length; i += 3) {
    const [x, y, z] = mat4Dot(mat, pl[i], pl[i + 1], pl[i + 2]);
    out[i] = x;
    out[i + 1] = y;
    out[i + 2] = z;
  }
  return out;
}

function transformLumberRail(rail: LumberRail, mat: Mat4): LumberRail {
  const newRail = transformPolyLine3(rail.rail, mat);
  const [normalX, normalY, normalZ] = mat4DotSub3(mat, rail.nX, rail.nY, rail.nZ);
  const [nX, nY, nZ] = vec3Normalize(normalX, normalY, normalZ);
  const clipPlanes = rail.clipPlanes?.map((plane) => transformPlane(planeCopy(plane), mat));
  return lumberRailNew(
    newRail,
    nX,
    nY,
    nZ,
    clipPlanes ?? undefined,
    rail.startExtension ?? undefined,
    rail.endExtension ?? undefined,
    rail.id,
  );
}

export function transformFramedWallAssemblyToPlane(
  assembly: GeneratedFramedWallAssembly,
  canonicalPlane: Plane,
  targetPlane: Plane,
): GeneratedFramedWallAssembly {
  // geom's planeToPlaneMat4 assumes orthonormal UV bases. The canonical plane is always one we
  // construct (planeNewXZ), so only the target plane — derived from the public framingPlane input —
  // needs validation against a scaled or sheared basis.
  assertOrthonormalPlane(targetPlane, "wall framing target plane");
  const mat = planeToPlaneMat4(canonicalPlane, targetPlane);
  const framing: Framing[] = assembly.framing.map((member) => ({
    ...member,
    rails: member.rails.map((rail) => transformLumberRail(rail, mat)),
  }));
  return {
    ...assembly,
    framing,
  };
}
